<script lang="ts">
    import * as Card from '@/components/ui/card';
    import { Button } from '@/components/ui/button';
    import { Plus, Pencil } from 'lucide-svelte';
    import type Blocks from '@/schemas/public/Blocks.ts';
    import type { Field } from '@/types/types';
    import type { DependencyMap } from '@/types/fieldDependency';
    import CreateScaffoldingHandoverModal from './CreateScaffoldingHandoverModal.svelte';

    type Signature = {
        id: string;
        signature_path: string;
        signed_by_name: string;
        signed_at?: string;
    };

    type Props = {
        jobId: string;
        scaffoldingHandover?: any & {
            inspector_signature?: Signature | null;
            contractor_signature?: Signature | null;
            client_signature?: Signature | null;
        };
        scaffoldingHandoverBlocks?: (Blocks & { fields: Field[] })[];
        scaffoldingHandoverDependencyMap?: DependencyMap;
        handoverPersonOptions?: { value: string; label: string }[];
    };

    let {
        jobId,
        scaffoldingHandover = null,
        scaffoldingHandoverBlocks = [],
        scaffoldingHandoverDependencyMap = {},
        handoverPersonOptions = []
    }: Props = $props();

    let showCreateModal = $state(false);
    let selectedHandover = $state<any>(null);

    function openCreateModal() {
        selectedHandover = null;
        showCreateModal = true;
    }

    function openEditModal(handover: any) {
        selectedHandover = handover;
        showCreateModal = true;
    }
</script>


<Card.Root>
    <Card.Content>
        <Card.Title class="flex items-center justify-between pb-4">
            Scaffolding Handover Form
            {#if !scaffoldingHandover}
                <Button
                    onclick={openCreateModal}
                    class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent ">
                    <Plus class="h-4 w-4" />
                    Add Handover Certificate
                </Button>
            {/if}
        </Card.Title>
        {#if scaffoldingHandover}
            <Card.Root>
                <Card.Header>
                    <Card.Title class="text-base">
                        {scaffoldingHandover.contractor || 'Scaffolding Handover'} - {scaffoldingHandover.handover_date || 'No date'}
                    </Card.Title>
                </Card.Header>
                <Card.Content class="space-y-2">
                    <div class="grid grid-cols-2 gap-4 text-sm">
                        {#if scaffoldingHandover.site}
                            <div><strong>Site:</strong> {scaffoldingHandover.site}</div>
                        {/if}
                        {#if scaffoldingHandover.handover_carried_out_by}
                            <div><strong>Handed Over By:</strong> {scaffoldingHandover.handover_carried_out_by}</div>
                        {/if}
                        {#if scaffoldingHandover.quotation_no}
                            <div><strong>Quotation No:</strong> {scaffoldingHandover.quotation_no}</div>
                        {/if}
                        {#if scaffoldingHandover.scaffold_contractor_name}
                            <div><strong>Scaffold Contractor:</strong> {scaffoldingHandover.scaffold_contractor_name}</div>
                        {/if}
                        {#if scaffoldingHandover.section_description_location}
                            <div class="col-span-2"><strong>Description:</strong> {scaffoldingHandover.section_description_location}</div>
                        {/if}
                    </div>

                    <!-- Safety Compliance Badges -->
                    <div class="flex gap-2 mt-4 flex-wrap">
                        {#if scaffoldingHandover.big_ben_scafflock_protected}
                            <span class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Big Ben Scafflock</span>
                        {/if}
                        {#if scaffoldingHandover.designed_for_wind_sails}
                            <span class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Wind Sails</span>
                        {/if}
                        {#if scaffoldingHandover.work_at_height_compliant}
                            <span class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">WAHR Compliant</span>
                        {/if}
                    </div>

                    <!-- Signatures Section -->
                    {#if scaffoldingHandover.inspector_signature || scaffoldingHandover.contractor_signature || scaffoldingHandover.client_signature}
                        <div class="mt-6 border-t pt-4">
                            <h4 class="text-sm font-semibold mb-3">Signatures</h4>
                            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {#if scaffoldingHandover.inspector_signature}
                                    <div class="border rounded p-3">
                                        <div class="text-xs text-muted-foreground mb-2">Handover Carried Out By</div>
                                        <img
                                            src={route('signatures.show', { signatureId: scaffoldingHandover.inspector_signature.id })}
                                            alt="Signature by {scaffoldingHandover.inspector_signature.signed_by_name || 'Inspector'}"
                                            class="h-16 border rounded bg-white p-1"
                                        />
                                        {#if scaffoldingHandover.inspector_signature.signed_by_name}
                                            <div class="text-xs mt-1">{scaffoldingHandover.inspector_signature.signed_by_name}</div>
                                        {/if}
                                    </div>
                                {/if}
                                {#if scaffoldingHandover.contractor_signature}
                                    <div class="border rounded p-3">
                                        <div class="text-xs text-muted-foreground mb-2">Contractor</div>
                                        <img
                                            src={route('signatures.show', { signatureId: scaffoldingHandover.contractor_signature.id })}
                                            alt="Signature by {scaffoldingHandover.contractor_signature.signed_by_name || 'Contractor'}"
                                            class="h-16 border rounded bg-white p-1"
                                        />
                                        {#if scaffoldingHandover.contractor_signature.signed_by_name}
                                            <div class="text-xs mt-1">{scaffoldingHandover.contractor_signature.signed_by_name}</div>
                                        {/if}
                                    </div>
                                {/if}
                                {#if scaffoldingHandover.client_signature}
                                    <div class="border rounded p-3">
                                        <div class="text-xs text-muted-foreground mb-2">Client</div>
                                        <img
                                            src={route('signatures.show', { signatureId: scaffoldingHandover.client_signature.id })}
                                            alt="Signature by {scaffoldingHandover.client_signature.signed_by_name || 'Client'}"
                                            class="h-16 border rounded bg-white p-1"
                                        />
                                        {#if scaffoldingHandover.client_signature.signed_by_name}
                                            <div class="text-xs mt-1">{scaffoldingHandover.client_signature.signed_by_name}</div>
                                        {/if}
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {/if}

                    <div class="flex gap-2 mt-4">
                        <Button
                            size="sm"
                            variant="outline"
                            onclick={() => openEditModal(scaffoldingHandover)}
                        >
                            <Pencil class="h-3 w-3 mr-2" />
                            View/Edit
                        </Button>
                    </div>
                </Card.Content>
            </Card.Root>
        {:else}
            <p class="text-muted-foreground text-sm">No scaffolding handover certificate yet. Click "Add Handover Certificate" to create one.</p>
        {/if}
    </Card.Content>
</Card.Root>

{#if showCreateModal}
    <CreateScaffoldingHandoverModal
        bind:show={showCreateModal}
        blocks={scaffoldingHandoverBlocks}
        dependencyMap={scaffoldingHandoverDependencyMap}
        {jobId}
        handover={selectedHandover}
        {handoverPersonOptions}
    />
{/if}
